import React from 'react';
import { motion, useReducedMotion, Variants } from 'framer-motion';
import { springConfig } from '../utils/animations';

interface CardProps {
  children: React.ReactNode;
  className?: string;
  hoverEffect?: boolean;
  animated?: boolean;
  delay?: number;
  onClick?: () => void;
}

const Card: React.FC<CardProps> = ({
  children,
  className = "",
  hoverEffect = true,
  animated = false,
  delay = 0,
  onClick
}) => {
  const prefersReducedMotion = useReducedMotion();

  // Card hover variants with enhanced shadow
  const cardVariants: Variants = {
    hidden: {
      opacity: 0,
      y: 20,
      scale: 0.98
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        ...springConfig.gentle,
        delay
      }
    },
    hover: {
      y: -4,
      scale: 1.01,
      transition: springConfig.snappy
    },
    tap: {
      scale: 0.98,
      transition: { duration: 0.1 }
    }
  };

  // Base classes
  const baseClasses = `
    bg-white dark:bg-catppuccin-surface0
    rounded-xl shadow-md
    border border-gray-50 dark:border-catppuccin-surface1
  `;

  // If reduced motion is preferred, render without animations
  if (prefersReducedMotion) {
    const hoverClasses = hoverEffect
      ? 'hover:shadow-lg transition-shadow duration-300'
      : '';

    return (
      <div
        className={`
          ${baseClasses}
          ${hoverClasses}
          ${className}
        `}
        onClick={onClick}
      >
        {children}
      </div>
    );
  }

  // With animations
  if (animated) {
    return (
      <motion.div
        className={`${baseClasses} ${className}`}
        variants={cardVariants}
        initial="hidden"
        animate="visible"
        whileHover={hoverEffect ? "hover" : undefined}
        whileTap={onClick ? "tap" : undefined}
        onClick={onClick}
        style={{ cursor: onClick ? 'pointer' : 'default' }}
      >
        {children}
      </motion.div>
    );
  }

  // Hover effect only (no entry animation)
  if (hoverEffect) {
    return (
      <motion.div
        className={`${baseClasses} ${className}`}
        whileHover="hover"
        whileTap={onClick ? "tap" : undefined}
        variants={{
          hover: cardVariants.hover,
          tap: cardVariants.tap
        }}
        onClick={onClick}
        style={{ cursor: onClick ? 'pointer' : 'default' }}
      >
        {children}
      </motion.div>
    );
  }

  // No animations
  return (
    <div
      className={`${baseClasses} ${className}`}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

export default Card;
